import { useLocation } from '@gatsbyjs/reach-router'
import React, { SyntheticEvent, useCallback, useEffect, useReducer } from 'react'
import styled from 'styled-components'

import { MenuAccordionCollapseProps } from '../MenuAccordionCollapse'
import { MenuAccordionToggleProps } from '../MenuAccordionToggle'
import * as Styled from './styled'

export interface MenuAccordionPropsPanel {
  type: 'panel'
  key: string
  toggleElement: React.FunctionComponentElement<MenuAccordionToggleProps>
  collapseElement: React.FunctionComponentElement<MenuAccordionCollapseProps>
}

export interface MenuAccordionPropsElement {
  type: 'element'
  key: string
  element: React.ReactElement
}

export type MenuAccordionPropsItem = MenuAccordionPropsPanel | MenuAccordionPropsElement

export type MenuAccordionProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'ref'> & {
  activeKey?: string
  items: MenuAccordionPropsItem[]
  onSelect?: (activeKey: string | undefined) => void
}

const MenuAccordion = React.memo(
  React.forwardRef<HTMLDivElement, MenuAccordionProps>(function MenuAccordion(
    { activeKey: activeKeyProp, className, items, onSelect, ...otherProps },
    ref,
  ) {
    const [state, dispatch] = useReducer(
      (
        prevState: { activeKey?: string },
        action:
          | { type: 'setActiveKey'; value: string | undefined }
          | { type: 'toggleActiveKey'; value: string | undefined },
      ) => {
        switch (action.type) {
          case 'setActiveKey':
            return { activeKey: action.value }
          case 'toggleActiveKey':
            return prevState.activeKey === action.value
              ? { activeKey: undefined }
              : { activeKey: action.value }
        }
      },
      { activeKey: activeKeyProp },
    )

    useEffect(() => {
      dispatch({ type: 'setActiveKey', value: activeKeyProp })
    }, [activeKeyProp])

    useEffect(() => {
      if (!onSelect) return
      onSelect(state.activeKey)
    }, [onSelect, state.activeKey])

    const location = useLocation()
    const getCurrentPath = useCallback(() => `${location.pathname}${location.search}`, [location])
    const isAccountPage = getCurrentPath().includes('/account')

    return (
      <div className={className} ref={ref} {...otherProps}>
        {items.reduce((acc: React.ReactElement[], member) => {
          if (member.type === 'element') {
            const { element, key } = member
            acc.push(<Styled.MenuAccordionItem key={key}>{element}</Styled.MenuAccordionItem>)
          }
          if (member.type === 'panel') {
            const { collapseElement, key, toggleElement } = member
            acc.push(
              <Styled.MenuAccordionItem
                key={key}
                $isAccountPage={isAccountPage && key === 'My Account'}
              >
                {React.cloneElement(toggleElement, {
                  isActive: key === state.activeKey,
                  onToggle: (e?: SyntheticEvent) => {
                    if (e) {
                      e.preventDefault()
                    }
                    dispatch({ type: 'toggleActiveKey', value: key })
                  },
                })}
                {React.cloneElement(collapseElement, {
                  isActive: key === state.activeKey,
                })}
              </Styled.MenuAccordionItem>,
            )
          }
          return acc
        }, [])}
      </div>
    )
  }),
)

MenuAccordion.displayName = 'MenuAccordion'

const StyledMenuAccordion = styled(MenuAccordion)`
  display: flex;
  flex-direction: column;
`

export { StyledMenuAccordion as MenuAccordion }
